import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import { Member } from 'src/modules/member/models/member';
import { getOtherMembers } from 'src/shared/services/memberService';
import { Person } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type CreateInvoiceMemberPickerProps = {
  onlyOtherMembers: boolean;
  label: string;
  selectedMemberCallback: (memberId: number) => void;
};

export const CreateInvoiceMemberPicker = (props: CreateInvoiceMemberPickerProps) => {
  const { onlyOtherMembers, label, selectedMemberCallback } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const [value, setValue] = React.useState<Member | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly Member[]>([]);

  const fetch = React.useMemo(
    () =>
      debounce(async (request: { input: string }, callback: (results?: readonly Member[]) => void) => {
        const result = await getOtherMembers(onlyOtherMembers, request.input);
        callback(result.data.results);
      }, 400),
    [onlyOtherMembers],
  );

  React.useEffect(() => {
    let active = true;

    fetch({ input: inputValue }, (results?: readonly Member[]) => {
      if (active && results !== undefined) {
        setOptions(results);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      size='small'
      fullWidth
      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, selected) => option.member_id === selected.member_id}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText={t('common:statusMessages.noSearchResult')}
      onChange={(event, newValue: Member | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue?.member_id) selectedMemberCallback(newValue?.member_id);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => <TextField {...params} label={label} fullWidth />}
      renderOption={(rendProps, option) => {
        return (
          <li {...rendProps} key={option.member_id} style={{ borderBottom: '1px dashed #eee', margin: 0 }}>
            <Grid container alignItems='center'>
              <Grid item sx={{ display: 'flex', width: 35 }}>
                <Person sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: `calc(100% - 35px${!onlyOtherMembers && ' - 10rem'})`, wordWrap: 'break-word' }}>
                <Typography variant='body1' color='text.primary'>
                  <Box component='span' sx={{ fontWeight: 600 }}>
                    {option.firstName} {option.lastName}
                  </Box>
                  {option.postAddress && <span>, {option.postAddress}</span>}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {[option.address, option.postNr, option.postAddress].filter((x) => !!x?.trim()).join(', ')}
                </Typography>
              </Grid>
              {!onlyOtherMembers && (
                <Grid item sx={{ width: '8rem' }} textAlign='right'>
                  <Typography variant='caption' color='text.secondary'>
                    {t('invoice:createInvoice.memberNr')}
                  </Typography>
                  <Typography variant='body1' color='text.primary' sx={{ fontWeight: 600 }}>
                    #{option.memberNr}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </li>
        );
      }}
    />
  );
};
